import React from "react";
import gsap from "gsap";
import { EyeIcon, HeadphonesIcon } from "lucide-react";

import HypemeterIcon from "@/assets/svg/onboarding/hypemeter.svg?react";
import DrakeAvatar from "@/assets/images/artist-drake.png";
import KendrickAvatar from "@/assets/images/artist-kendrick.png";
import EminemAvatar from "@/assets/images/artist-eminem.png";
import { HypeboardItem } from "../HypeboardItem";
import { cn } from "@/helpers/tailwind";
import { formatCurrency } from "@/helpers";
import { useCarousel } from "@/components/ui";

const EMINEM_INITIAL_HYPEMETER = 46;
const mockArtists = [
  {
    rank: 1,
    hypeMeter: 92,
    artist: {
      id: 0,
      image: DrakeAvatar,
      name: "Drake",
      country: "USA",
    },
  },
  {
    rank: 2,
    hypeMeter: 83,
    artist: {
      id: 1,
      image: KendrickAvatar,
      name: "Kendrick Lamar",
      country: "USA",
    },
  },
  {
    rank: 3,
    hypeMeter: EMINEM_INITIAL_HYPEMETER,
    artist: {
      id: 2,
      image: EminemAvatar,
      name: "Eminem",
      country: "USA",
    },
  },
];

const numberMetrics = [
  {
    title: "Streams",
    amount: 1000,
    icon: <HeadphonesIcon className="size-8" />,
    artist: mockArtists[2].artist,
  },
  {
    title: "Video views",
    amount: 500,
    icon: <EyeIcon className="size-8" />,
    artist: mockArtists[2].artist,
  },
];

const FINAL_EMINEM_HYPEMETER = 84;
const DURATION = 2;

interface ArtistMetricsProps {
  className?: string;
}

const ArtistMetrics: React.FC<ArtistMetricsProps> = ({ className }) => {
  const { api } = useCarousel();
  const numberRefs = React.useRef<(HTMLParagraphElement | null)[]>([]);
  const timelineRef = React.useRef<gsap.core.Timeline | null>(null);

  React.useEffect(() => {
    if (!timelineRef.current) {
      timelineRef.current = gsap.timeline({ paused: true });

      // Create a single animation that targets both numbers
      const tweens = numberRefs.current
        .map((ref, index) => {
          if (!ref) return null;
          const amount = numberMetrics[index].amount;
          let start = Math.floor(amount / 2);
          const end = amount;

          return gsap.to(ref, {
            duration: DURATION,
            onUpdate: function () {
              const progress = this.progress();
              const current = Math.round(start + progress * (end - start));
              ref.textContent = `+ ${formatCurrency(current, 0)}`;
            },
            ease: "expo.out",
          });
        })
        .filter((tween): tween is gsap.core.Tween => tween !== null);

      // Add all tweens to the timeline at the same time
      timelineRef.current.add(tweens);
    }

    if (!api) return;

    const handleSlideChange = () => {
      const currentSlide = api?.selectedScrollSnap();

      if (currentSlide === 0) {
        timelineRef.current?.play(0);
      } else {
        timelineRef.current?.reverse(0.5);
      }
    };

    // Play animation on mount if we're on slide 0
    if (api?.selectedScrollSnap() === 0) {
      timelineRef.current?.play(0);
    }

    api.on("select", handleSlideChange);
    return () => {
      api.off("select", handleSlideChange);
    };
  }, [api]);

  return (
    <div className={className}>
      {numberMetrics.map(({ title, amount, icon, artist }, index) => (
        <div
          key={index}
          className={cn(
            "flex items-center gap-3 p-3 lg:p-8",
            "basis-1/2 justify-center lg:basis-auto lg:justify-start",
            {
              "border-r border-indigo-400/20 lg:border-b lg:border-r-0":
                index === 0,
            }
          )}>
          <div className="flex flex-grow flex-col gap-2">
            <div
              className={cn(
                "relative size-[42px] overflow-hidden",
                "rounded-lg border border-indigo-400"
              )}>
              <img
                className="absolute left-0 top-0 size-[42px] object-cover"
                src={artist.image}
                alt={artist.name}
              />
            </div>
            <p
              className={cn(
                "whitespace-nowrap font-phonk shadow-lg",
                "text-lg lg:min-w-[115px] lg:text-2xl"
              )}
              ref={(el) => (numberRefs.current[index] = el)}>
              + {formatCurrency(amount, 0)}
            </p>
            <p className="text-xs text-slate-200 lg:text-sm">{title}</p>
          </div>
          <div
            className={cn(
              "mb-6 size-9 rounded-full rounded-bl-none p-2 lg:size-16",
              "flex items-center justify-center",
              "bg-indigo-400/20 text-indigo-400"
            )}>
            {icon}
          </div>
        </div>
      ))}
    </div>
  );
};

interface HypeMeterProps {
  className?: string;
}

const HypeMeter: React.FC<HypeMeterProps> = ({ className }) => {
  const { api } = useCarousel();
  const hypemeterRef = React.useRef<HTMLDivElement>(null);
  const timelineRef = React.useRef<gsap.core.Timeline | null>(null);

  React.useEffect(() => {
    if (!timelineRef.current) {
      const needle = hypemeterRef.current?.querySelector("g.hypemeter-needle");
      const gradientContent = hypemeterRef.current?.querySelector(
        ".hypemeter-gradient-content"
      );

      if (!needle || !gradientContent) {
        console.log(
          "Could not find required elements to begin Hypemeter animation"
        );
        return;
      }

      const rotationPoint = "170 120";
      const timeline = gsap.timeline({
        defaults: { duration: DURATION, ease: "power2.inOut" },
        delay: 0.5,
      });

      // Set initial positions
      gsap.set([needle, gradientContent], {
        rotation: -45,
        svgOrigin: rotationPoint,
        transformOrigin: "170px 120px",
      });

      timelineRef.current = gsap.timeline({
        paused: true,
        defaults: { duration: DURATION, ease: "power2.inOut" },
      });

      // Animate with delay for reverse
      timelineRef.current.to([needle, gradientContent], {
        rotation: 0,
        svgOrigin: rotationPoint,
        transformOrigin: "170px 120px",
        delay: 0.5, // This will be applied when reversing
      });
    }

    if (!api) return;

    const handleSlideChange = () => {
      const currentSlide = api?.selectedScrollSnap();

      if (currentSlide === 0) {
        timelineRef.current?.play(0);
      } else {
        timelineRef.current?.reverse();
      }
    };

    // Play animation on mount if we're on slide 0
    if (api?.selectedScrollSnap() === 0) {
      timelineRef.current?.play(0);
    }

    api.on("select", handleSlideChange);
    return () => {
      api.off("select", handleSlideChange);
      timelineRef.current?.kill();
    };
  }, [api]);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-6",
        "self-stretch px-8 py-3 text-center"
      )}>
      <p className="font-phonk text-4xl text-indigo-400/20">Hypemeter</p>
      <div ref={hypemeterRef}>
        <HypemeterIcon className="max-w-[280px] lg:max-w-none" />
      </div>
    </div>
  );
};

interface HypeLeaderboardCardsProps {
  className?: string;
  variant?: "desktop" | "mobile";
  beginAnimation?: boolean;
  animationDuration?: number;
  animationEase?: gsap.EaseString;
}

const HypeLeaderboardCards: React.FC<HypeLeaderboardCardsProps> = ({
  className,
  variant = "mobile",
  beginAnimation = false,
  animationDuration,
  animationEase = "expo.out",
}) => {
  const { api } = useCarousel();
  const [eminemHype, setEminemHype] = React.useState(EMINEM_INITIAL_HYPEMETER);
  const timelineRef = React.useRef<gsap.core.Timeline | null>(null);
  const animationHasFinished = React.useMemo(
    () => eminemHype === FINAL_EMINEM_HYPEMETER,
    [eminemHype]
  );

  React.useEffect(() => {
    if (!timelineRef.current) {
      timelineRef.current = gsap.timeline({
        paused: true,
        defaults: {
          duration: animationDuration ?? DURATION * 2,
          ease: animationEase,
        },
      });

      timelineRef.current.to(
        { value: EMINEM_INITIAL_HYPEMETER },
        {
          value: FINAL_EMINEM_HYPEMETER,
          onUpdate: function () {
            setEminemHype(Math.round(this.targets()[0].value));
          },
        }
      );
    }

    if (!api) return;

    const handleSlideChange = () => {
      const currentSlide = api?.selectedScrollSnap();

      if (currentSlide === 0) {
        timelineRef.current?.play(0);
      } else {
        timelineRef.current?.reverse();
      }
    };

    // Play animation on mount if we're on slide 0 or beginAnimation is true
    if (api?.selectedScrollSnap() === 0 || beginAnimation) {
      timelineRef.current?.play(0);
    }

    api.on("select", handleSlideChange);
    return () => {
      api.off("select", handleSlideChange);
      timelineRef.current?.kill();
    };
  }, [api, beginAnimation, animationDuration, animationEase]);

  const getRank = (rank: number, artistId: number) => {
    if (animationHasFinished) {
      if (artistId === 1) return 3;
      if (artistId === 2) return 2;
    }
    return rank;
  };

  return (
    <div className={cn("flex items-center justify-center", className)}>
      {variant === "mobile" && (
        <p className="font-phonk text-4xl text-slate-800">Hypeboard</p>
      )}
      <div className="flex flex-col gap-3">
        {mockArtists.map(({ rank, hypeMeter, artist }, index) => (
          <div
            key={index}
            className={cn(
              "w-full opacity-100",
              "transition-all duration-0 ease-in-out",
              {
                "duration-700": animationHasFinished,
                "translate-y-[calc(100%+12px)]":
                  animationHasFinished && artist.id === 1,
                "-translate-y-[calc(100%+12px)]":
                  animationHasFinished && artist.id === 2,
                "opacity-20 duration-500":
                  !animationHasFinished &&
                  artist.id !== 2 &&
                  eminemHype >
                    EMINEM_INITIAL_HYPEMETER +
                      (FINAL_EMINEM_HYPEMETER - EMINEM_INITIAL_HYPEMETER) * 0.2,
              }
            )}
            style={{ willChange: "transform" }}>
            <HypeboardItem
              className="w-full"
              rank={getRank(rank, artist.id)}
              artist={artist}
              hypeMeter={artist.id === 2 ? eminemHype : hypeMeter}
            />
          </div>
        ))}
      </div>
      {variant === "desktop" && (
        <p className="writing-vertical-lr rotate-180 font-phonk text-4xl text-slate-800">
          Hypeboard
        </p>
      )}
    </div>
  );
};

export const OnboardingStepDesktop1: React.FC = () => {
  return (
    <div className="flex w-full items-center rounded-3xl bg-slate-900">
      <ArtistMetrics className="flex basis-[26%] flex-col" />
      <HypeMeter className="basis-[35%] border-l border-r border-indigo-400/20" />
      <HypeLeaderboardCards
        className="max-w-[446px] basis-[39%] gap-2 px-6 py-8"
        variant="desktop"
      />
    </div>
  );
};

export const OnboardingStepMobile1: React.FC = () => {
  const { api } = useCarousel();
  const [showSecondScreen, setShowSecondScreen] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    if (!api) return;

    const handleSlideChange = () => {
      const currentSlide = api?.selectedScrollSnap();

      if (currentSlide === 0) {
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        // Start new timeout
        timeoutRef.current = setTimeout(
          () => setShowSecondScreen(true),
          (DURATION + 2) * 1000
        );
      } else {
        // Clear timeout and reset state when leaving slide
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        setShowSecondScreen(false);
      }
    };

    // Initial check for slide 0
    if (api?.selectedScrollSnap() === 0) {
      timeoutRef.current = setTimeout(
        () => setShowSecondScreen(true),
        (DURATION + 2) * 1000
      );
    }

    api.on("select", handleSlideChange);
    return () => {
      api.off("select", handleSlideChange);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [api]);

  return (
    <div className="relative flex w-full flex-col items-center rounded-3xl bg-slate-900">
      <div
        className={cn(
          "w-full opacity-100 transition-opacity duration-300 ease-in-out",
          { "pointer-events-none opacity-0": showSecondScreen }
        )}>
        <ArtistMetrics
          className={cn(
            "flex w-full justify-center border-b border-indigo-400/20"
          )}
        />
        <HypeMeter />
      </div>

      <HypeLeaderboardCards
        className={cn(
          "w-full flex-col gap-3 px-3 pb-6 pt-3",
          "absolute left-0 top-0",
          "opacity-100 transition-opacity delay-150 duration-300 ease-in-out",
          { "pointer-events-none opacity-0": !showSecondScreen }
        )}
        beginAnimation={showSecondScreen}
        animationDuration={DURATION + 0.5}
        animationEase="power4.inOut"
      />
    </div>
  );
};
